import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { buildMediaUrl } from "../lib/api";
import { ArrowUpRight, AlertTriangle, Eye } from "lucide-react";

const VIEWS_LABEL = {
  ar: "مشاهدة",
  en: "views",
  fr: "vues",
  he: "צפיות",
  de: "Aufrufe",
};

function formatViews(n, lang) {
  const num = typeof n === "number" ? n : 0;
  const display = num >= 1000 ? `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}k` : `${num}`;
  return `${display} ${VIEWS_LABEL[lang] || VIEWS_LABEL.en}`;
}

const HERO_FALLBACKS = {
  war_crime: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/07d06b03700bad39a92b3777f5a8f0215faa452d7438dcc85358c0f56db0d3eb.png",
  terror_crime: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/af3383f01b902e9c59eacfbaee9a4c68f6f87ee413498a382bce77842fa0897b.png",
  monitoring: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/af3383f01b902e9c59eacfbaee9a4c68f6f87ee413498a382bce77842fa0897b.png",
  study: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/d674fcde06ed8375649c122b40877b2edc71287780b0b40fbaaf78fac4093ff5.png",
  analysis: "https://images.unsplash.com/photo-1768213022263-0414dc145dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  news: "https://images.unsplash.com/photo-1758138258458-64ee0fbdef4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  director_message: "https://static.prod-images.emergentagent.com/jobs/be2064b9-9d7d-4260-b3d4-e04b780abb55/images/d674fcde06ed8375649c122b40877b2edc71287780b0b40fbaaf78fac4093ff5.png",
  publication: "https://images.unsplash.com/photo-1598420563273-df02d9118be7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export default function ArticleCard({ article, variant = "default" }) {
  const { t, pickArticle, lang } = useLang();
  const block = pickArticle(article);
  const date = article.publish_date ? new Date(article.publish_date) : null;
  const dateStr = date ? date.toLocaleDateString(lang === "ar" ? "ar-EG" : lang === "fr" ? "fr-FR" : "en-GB", { year: "numeric", month: "short", day: "2-digit" }) : "";
  const img = article.featured_image
    ? buildMediaUrl(article.featured_image)
    : HERO_FALLBACKS[article.category] || HERO_FALLBACKS.analysis;
  const catLabel = t.categories[article.category] || article.category;

  if (variant === "lead") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block tile p-0"
        data-testid={`article-lead-${article.slug}`}
      >
        <div className="grid lg:grid-cols-12 gap-0">
          <div className="lg:col-span-7 aspect-[16/9] lg:aspect-auto lg:h-[420px] bg-slate100 overflow-hidden">
            <img
              src={img}
              alt={block.title}
              className="w-full h-full object-cover group-hover:opacity-95 transition-opacity"
            />
          </div>
          <div className="lg:col-span-5 p-7 lg:p-10 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-4">
              <span className="label-eyebrow text-ink">{catLabel}</span>
              {article.is_breaking && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-crimson">
                  <AlertTriangle className="w-3 h-3" /> Breaking
                </span>
              )}
            </div>
            <h2 className="heading-serif text-3xl lg:text-4xl text-ink leading-[1.1] mb-4 group-hover:opacity-80 transition-opacity">
              {block.title}
            </h2>
            <p className="text-[14px] text-slate600 leading-relaxed line-clamp-3 mb-6">
              {block.summary}
            </p>
            <div className="flex items-center justify-between text-[11px] text-slate500 uppercase tracking-wider">
              <span className="flex items-center gap-3">
                <span>{dateStr}</span>
                <span className="inline-flex items-center gap-1 text-slate600" data-testid={`views-${article.slug}`} title={formatViews(article.views, lang)}>
                  <Eye className="w-3 h-3" />
                  {formatViews(article.views, lang)}
                </span>
              </span>
              <span className="inline-flex items-center gap-1 text-ink font-medium">
                {t.home.read_more} <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="block tile p-5 group"
        data-testid={`article-compact-${article.slug}`}
      >
        <div className="flex items-center gap-3 mb-3">
          <span className="label-eyebrow text-ink">{catLabel}</span>
          {article.is_breaking && (
            <span className="text-[10px] uppercase tracking-widest font-semibold text-crimson">●</span>
          )}
        </div>
        <h3 className="heading-serif text-lg text-ink leading-snug group-hover:opacity-80 transition-opacity mb-2">
          {block.title}
        </h3>
        <div className="flex items-center justify-between text-[11px] text-slate500 uppercase tracking-wider">
          <span>{dateStr}</span>
          <span className="inline-flex items-center gap-1 text-slate600" data-testid={`views-${article.slug}`}>
            <Eye className="w-3 h-3" />
            {formatViews(article.views, lang)}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block tile overflow-hidden"
      data-testid={`article-card-${article.slug}`}
    >
      <div className="aspect-[16/10] bg-slate100 overflow-hidden">
        <img
          src={img}
          alt={block.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-2">
          <span className="label-eyebrow text-ink">{catLabel}</span>
          {article.country && (
            <span className="text-[10px] uppercase tracking-widest text-slate500">{article.country}</span>
          )}
          {article.organization && (
            <span className="text-[10px] uppercase tracking-widest text-slate500">{article.organization}</span>
          )}
        </div>
        <h3 className="heading-serif text-lg text-ink leading-snug mb-2 group-hover:opacity-80 transition-opacity">
          {block.title}
        </h3>
        <p className="text-[13px] text-slate600 leading-relaxed line-clamp-2 mb-4">
          {block.summary}
        </p>
        <div className="flex items-center justify-between text-[11px] text-slate500 uppercase tracking-wider">
          <span className="flex items-center gap-3">
            <span>{dateStr}</span>
            <span className="inline-flex items-center gap-1 text-slate600" data-testid={`views-${article.slug}`}>
              <Eye className="w-3 h-3" />
              {formatViews(article.views, lang)}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-ink font-medium">
            {t.home.read_more} <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}
